import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@ui/lib/utils.js';

export { default as PButton, type PButtonProps } from './PButton.vue';

const pButtonCvaConfig = {
    variants: {
        variant: {
            default: cn(
                'bg-surface-primary',
                'text-foreground-on-primary',
                'hover:bg-surface-primary-hover',
                'active:bg-surface-primary-active',
            ),
            destructive: cn(
                'bg-surface-destructive',
                'text-foreground-on-destructive',
                'focus-visible:ring-ring-error',
            ),
            outline: cn(
                'border',
                'bg-surface-outline',
                'text-foreground-primary',
                'hover:bg-surface-outline-hover',
                'active:bg-surface-outline-active',
                'border-border-border-3',
                'focus-visible:border-border-border-4',
            ),
            secondary: cn(
                'bg-surface-secondary',
                'text-foreground-on-secondary',
                'hover:bg-surface-secondary-hover',
                'active:bg-surface-secondary-active',
            ),
            ghost: cn(
                'bg-surface-ghost',
                'text-foreground-on-ghost',
                'hover:bg-surface-ghost-hover',
                'active:bg-surface-ghost-active',
            ),
        },
        size: {
            default: 'h-9 px-4 py-2 has-[>svg]:px-3',
            lg: 'h-10 px-6 py-2 has-[>svg]:px-4',
            sm: 'h-8 px-3 has-[>svg]:px-2.5',
            xs: 'h-6 px-2 has-[>svg]:px-2 paragraph-xs',
            icon: 'size-9 p-2',
            'icon-lg': 'size-10 p-2.5',
            'icon-sm': 'size-8 p-2',
            'icon-xs': 'size-6 p-1',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
} as const;

export const pButtonVariants = cva(
    cn(
        'inline-flex',
        'items-center',
        'justify-center',
        'gap-2',
        'whitespace-nowrap',
        'rounded-lg',
        'paragraph-sm',
        'font-medium',
        'transition',
        'disabled:pointer-events-none',
        '[&_svg]:pointer-events-none',
        "[&_svg:not([class*='size-'])]:size-4",
        'shrink-0',
        '[&_svg]:shrink-0',
        'outline-none',
        'focus-visible:ring-ring-default',
        'focus-visible:ring-3',
        'disabled:opacity-50',
    ),
    pButtonCvaConfig,
);

export const P_BUTTON_VARIANTS = Object.keys(pButtonCvaConfig.variants.variant);
export const P_BUTTON_SIZES = Object.keys(pButtonCvaConfig.variants.size);
export type PButtonVariant = keyof typeof pButtonCvaConfig.variants.variant;
export type PButtonSize = keyof typeof pButtonCvaConfig.variants.size;
export type PButtonVariants = VariantProps<typeof pButtonVariants>;
